import type { SellerLimitsDTO } from '@sellsen/generated-sources';

/**
 * Satıcı kotası uyarı şeridinin saf kuralları (bileşenden bağımsız, test edilebilir).
 *
 * Kapsam yalnız ekleme işlemini ENGELLEYEN iki kotadır: katalog (ürün havuzu + envanter) ve
 * ASIN havuzu. Günlük arama kotası bilinçli olarak dışarıda: kendiliğinden sıfırlanır ve arama
 * ekranı reddi zaten gösterir.
 *
 * Eşikler: %90'dan itibaren sarı (kapatılabilir), %100 ve üstü kırmızı (kapatılamaz). %100'ün
 * üstü mümkündür; envanter sayısı Amazon raporundan gelir ve limitle kesilmez.
 */
export const SELLER_LIMIT_WARN_RATIO = 0.9;

export type SellerLimitKind = 'catalog' | 'asinPool';
export type SellerLimitSeverity = 'warning' | 'error';

export interface SellerLimitBannerModel {
  kind: SellerLimitKind;
  severity: SellerLimitSeverity;
  limit: number;
  used: number;
  /** Limite kadar kalan; dolmuşsa 0. */
  remaining: number;
  /** Limiti aşan adet; dolmamışsa 0. Kırmızı şeritte "en az N silin" değeri. */
  overBy: number;
  /** Tam sayı yüzde, 100'ü aşabilir. */
  percent: number;
}

/** Backend `ErrorResponse.code` → kota türü. `SellerLimitService` sabitleriyle aynı. */
export const SELLER_LIMIT_CODE_KIND: Record<string, SellerLimitKind> = {
  SELLER_LIMIT_CATALOG: 'catalog',
  SELLER_LIMIT_ASIN_POOL: 'asinPool',
};

export const sellerLimitKindFromCode = (
  code: string | undefined
): SellerLimitKind | undefined =>
  code ? SELLER_LIMIT_CODE_KIND[code] : undefined;

/** Kotayı açmak için kullanıcının gideceği sayfa. */
export const SELLER_LIMIT_TARGET: Record<
  SellerLimitKind,
  { label: string; path: string }
> = {
  catalog: { label: 'Ürün havuzuna git', path: '/product-staging' },
  asinPool: { label: 'ASIN havuzuna git', path: '/asin-pool' },
};

/** Envanter sayfasını inaktif temizlik modalı açık başlatır (bkz. pages/inventories). */
export const INACTIVE_CLEANUP_PATH = '/inventories?cleanup=1';

export const SELLER_LIMIT_KIND_LABEL: Record<SellerLimitKind, string> = {
  catalog: 'Katalog',
  asinPool: 'ASIN havuzu',
};

const evaluateOne = (
  kind: SellerLimitKind,
  limit: number | undefined,
  used: number | undefined
): SellerLimitBannerModel | null => {
  if (!limit || limit <= 0) {
    return null;
  }
  const safeUsed = Math.max(0, used ?? 0);
  const ratio = safeUsed / limit;
  if (ratio < SELLER_LIMIT_WARN_RATIO) {
    return null;
  }
  return {
    kind,
    severity: ratio >= 1 ? 'error' : 'warning',
    limit,
    used: safeUsed,
    remaining: Math.max(0, limit - safeUsed),
    overBy: Math.max(0, safeUsed - limit),
    percent: Math.round(ratio * 100),
  };
};

/**
 * Gösterilecek şeritler, kırmızılar önce. `dismissed` yalnız sarıyı gizler: kullanım %100'e
 * ulaştığında daha önce kapatılmış olması geçersizdir.
 */
export const evaluateSellerLimitBanners = (
  limits: SellerLimitsDTO | null | undefined,
  dismissed: ReadonlySet<SellerLimitKind> = new Set()
): SellerLimitBannerModel[] => {
  if (!limits) {
    return [];
  }
  const candidates = [
    evaluateOne('catalog', limits.catalogProductLimit, limits.catalogProductUsed),
    evaluateOne('asinPool', limits.asinPoolLimit, limits.asinPoolUsed),
  ];
  return candidates
    .filter((b): b is SellerLimitBannerModel => b !== null)
    .filter((b) => b.severity === 'error' || !dismissed.has(b.kind))
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
};

const severityRank = (s: SellerLimitSeverity): number =>
  s === 'error' ? 0 : 1;

const formatCount = (value: number): string => value.toLocaleString('tr-TR');

/** Şerit başlığı: önce sonuç (ne durdu), sayılar açıklamada. */
export const sellerLimitBannerTitle = (b: SellerLimitBannerModel): string => {
  if (b.kind === 'catalog') {
    return b.severity === 'error'
      ? 'Katalog kotanız doldu. Taramadan ürün havuzuna aktarma ve otomatik filtre aktarımları durdu.'
      : `Katalog kotası %${b.percent} dolu. Dolunca taramadan ürün havuzuna aktarma durur.`;
  }
  return b.severity === 'error'
    ? 'ASIN havuzu kotanız doldu. Tarama sonuçlarının ve uzantının ASIN havuzuna eklemesi durdu.'
    : `ASIN havuzu kotası %${b.percent} dolu. Dolunca havuza ekleme durur.`;
};

/** Açıklama satırı: paket, izin verilen, mevcut, kalan/aşım ve yapılacak iş. */
export const sellerLimitBannerDescription = (
  b: SellerLimitBannerModel,
  tier?: string | null
): string => {
  const facts: string[] = [];
  if (tier) {
    facts.push(`Paket: ${tier}`);
  }
  facts.push(`İzin verilen: ${formatCount(b.limit)}`);
  facts.push(`Mevcut: ${formatCount(b.used)}`);
  if (b.severity === 'error') {
    const unit = b.kind === 'catalog' ? 'ürün' : 'ASIN';
    const action = `Devam etmek için en az ${formatCount(Math.max(1, b.overBy))} ${unit} silin.`;
    const hint =
      b.kind === 'catalog'
        ? ' Envanterdeki inaktif ürünler ve havuzda bekleyen eski taramalar kotayı açar.'
        : '';
    return `${facts.join(' · ')}. ${action}${hint}`;
  }
  facts.push(`Kalan: ${formatCount(b.remaining)}`);
  return `${facts.join(' · ')}.`;
};
